/**
 * Reusable controls for product/list cards (founder 2026-06-25): search, click-to-sort
 * column headers, a "Show all N" expander, and SKU codes shown under product names.
 * Applied across every applicable box so product data is explorable everywhere.
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useApiClient } from "../lib/api-context.js";
import { productSkuLabel, type ProductSkuLabel } from "../lib/product-sku-label.js";

/**
 * A drag-to-resize width for a RIGHT-hand panel (the drill filter rail). Returns
 * the current width + a mousedown handler for the drag grip on the panel's LEFT
 * edge — dragging left widens it. Persisted per storageKey so it sticks (founder
 * 2026-06-25: "let me widen the sidebar so I can see the full product/SKU name").
 */
export function useResizableWidth(initial: number, storageKey: string, min = 220, max = 620): { width: number; onGripDown: (e: React.MouseEvent) => void } {
  const [width, setWidth] = useState<number>(() => {
    if (typeof window === "undefined") return initial;
    const saved = Number(window.localStorage.getItem(storageKey));
    return Number.isFinite(saved) && saved >= min && saved <= max ? saved : initial;
  });
  useEffect(() => { try { window.localStorage.setItem(storageKey, String(Math.round(width))); } catch { /* ignore */ } }, [width, storageKey]);
  const onGripDown = (e: React.MouseEvent): void => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = width;
    const onMove = (ev: MouseEvent): void => setWidth(Math.min(max, Math.max(min, startW + (startX - ev.clientX))));
    const onUp = (): void => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };
  return { width, onGripDown };
}

/** A family (breakdown product key) with its member product names + SKU codes. */
export interface ProductFamily {
  family: string;
  products: string[];
  skus: string[];
  skuDisplayByCode?: Record<string, ProductSkuLabel>;
}
interface ProductSkuIdentity {
  productName: string;
  variantName: string;
  classificationVariant: string | null;
  offerLabel: string | null;
  packSize: number | null;
  boxesPerSale: number | null;
}
export interface ProductSkusData {
  byProduct: Record<string, string[]>;
  byGroup: Record<string, string[]>;
  skuIdByCode: Record<string, string>;
  skuIdentityByCode?: Record<string, ProductSkuIdentity>;
  hierarchy?: ProductFamily[];
  reportingHierarchy?: ProductFamily[];
}

/** The shared, cached account-wide product↔SKU map (one fetch for all consumers). */
export function useProductSkusQuery(): UseQueryResult<ProductSkusData, Error> {
  const api = useApiClient();
  return useQuery({
    queryKey: ["product-skus"],
    queryFn: () => api.get<ProductSkusData>("/dashboard/product-skus"),
    staleTime: 10 * 60 * 1000,
  });
}

/** Product name (or overlay group name) → its SKU codes. Cached, account-wide. */
export function useProductSkus(): Record<string, string[]> {
  const data = useProductSkusQuery().data;
  return useMemo(() => buildProductSkus(data), [data]);
}

export function buildProductSkus(data: ProductSkusData | undefined): Record<string, string[]> {
  // byProduct (UltraCart product name) wins; byGroup (overlay family) fills gaps,
  // so a label keyed either way resolves to its SKUs.
  return { ...(data?.byGroup ?? {}), ...(data?.byProduct ?? {}) };
}

/** SKU code → its SKU id, for grouping SKUs via the /catalog endpoints (which take
 *  ids) from a card that only knows codes. Cached with the same fetch as above. */
export function useSkuIdByCode(): Record<string, string> {
  return useProductSkusQuery().data?.skuIdByCode ?? {};
}

/** The family → products/SKUs hierarchy for the drill's grouped product filter. */
export function useProductHierarchy(): ProductFamily[] {
  const data = useProductSkusQuery().data;
  return useMemo(() => buildProductHierarchy(data), [data]);
}

/** Keep the raw SKU code as the filter value, but give the person a useful label. */
export function buildProductHierarchy(data: ProductSkusData | undefined): ProductFamily[] {
  const hierarchy = data?.reportingHierarchy ?? data?.hierarchy ?? [];
  const allDisplays = buildSkuDisplayByCode(data);
  return hierarchy.map((family) => ({
    ...family,
    skuDisplayByCode: Object.fromEntries(family.skus.map((skuCode) => [skuCode, allDisplays[skuCode] ?? {
      description: family.family,
      details: "",
      code: `SKU ${skuCode}`,
    }])),
  }));
}

/** Account-wide readable labels for charts/tables whose stable row key is a SKU code. */
export function useSkuDisplayByCode(): Record<string, ProductSkuLabel> {
  const data = useProductSkusQuery().data;
  return useMemo(() => buildSkuDisplayByCode(data), [data]);
}

export function buildSkuDisplayByCode(data: ProductSkusData | undefined): Record<string, ProductSkuLabel> {
  const hierarchy = data?.reportingHierarchy ?? data?.hierarchy ?? [];
  const familyByCode = new Map(hierarchy.flatMap((family) => family.skus.map((code) => [code, family.family] as const)));
  const codes = new Set([
    ...Object.keys(data?.skuIdentityByCode ?? {}),
    ...familyByCode.keys(),
  ]);
  return Object.fromEntries([...codes].map((skuCode) => {
    const identity = data?.skuIdentityByCode?.[skuCode];
    return [skuCode, identity
      ? productSkuLabel({ skuCode, ...identity })
      : {
        description: familyByCode.get(skuCode) ?? skuCode,
        details: "",
        code: `SKU ${skuCode}`,
      }];
  }));
}

/** SKU code → the product/family name it belongs to (the inverse of useProductSkus),
 *  for showing a SKU's product name beside its code in SKU-level views. */
export function useSkuToName(): Record<string, string> {
  const byName = useProductSkus();
  return useMemo(() => buildSkuToName(byName), [byName]);
}

export function buildSkuToName(byName: Record<string, string[]>): Record<string, string> {
  const inv: Record<string, string> = {};
  for (const [name, codes] of Object.entries(byName)) {
    for (const code of codes) inv[code] = name; // byProduct-merged map already prefers the native product name
  }
  return inv;
}

/**
 * The muted SKU line shown under a product name. Null when there are no codes.
 * `cap` truncates a long list to the first N codes + "+M more" (click to expand) —
 * so a family with 60 SKUs never walls the row (founder 2026-06-26).
 */
export function SkuSub({ skus, cap }: { skus: string[] | undefined; cap?: number }): React.ReactElement | null {
  const [open, setOpen] = useState(false);
  if (!skus || skus.length === 0) return null;
  const truncated = cap !== undefined && !open && skus.length > cap;
  const shown = truncated ? skus.slice(0, cap) : skus;
  return (
    <div className="dd-num" style={{ fontSize: 10.5, color: "var(--faint)", fontWeight: 400, marginTop: 2, whiteSpace: "normal", lineHeight: 1.35 }}>
      {shown.join(" · ")}
      {truncated && (
        <>
          {" · "}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setOpen(true); }}
            style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--accent, #2563eb)", font: "inherit" }}
          >
            +{skus.length - cap} more
          </button>
        </>
      )}
    </div>
  );
}


export interface SortState { key: string; dir: "asc" | "desc" }

/**
 * Search + sort + see-more for a row list. The caller supplies how to read a row's
 * search text and the value for any sort key; this returns the visible slice plus
 * the control state. Cap defaults to 12; "Show all" reveals the rest.
 */
export function useListControls<T>(
  rows: T[] | undefined,
  opts: {
    searchText: (r: T) => string;
    sortVal: (r: T, key: string) => string | number | null | undefined;
    defaultSort?: SortState;
    cap?: number;
  },
): {
  q: string; setQ: (v: string) => void;
  sort: SortState | null; toggleSort: (key: string, numeric?: boolean) => void;
  visible: T[]; total: number; matched: number;
  showAll: boolean; setShowAll: (v: boolean) => void; cap: number;
} {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<SortState | null>(opts.defaultSort ?? null);
  const [showAll, setShowAll] = useState(false);
  const cap = opts.cap; // undefined = no cap (show every row; pair with a scroll box)
  const all = rows ?? [];
  const matched = useMemo(() => {
    // Tag-style AND search: split on spaces or "+" and require EVERY term (founder
    // 2026-07-01: "b1g1 + biot" = all buy-1-get-1 Biotin). One term behaves as before.
    const terms = q.trim().toLowerCase().split(/[\s+]+/).filter(Boolean);
    if (terms.length === 0) return all;
    return all.filter((r) => {
      const hay = opts.searchText(r).toLowerCase();
      return terms.every((t) => hay.includes(t));
    });
  }, [all, q]);
  const sorted = useMemo(() => {
    if (!sort) return matched;
    const { key, dir } = sort;
    return [...matched].sort((a, b) => {
      const av = opts.sortVal(a, key), bv = opts.sortVal(b, key);
      const aNull = av === null || av === undefined, bNull = bv === null || bv === undefined;
      if (aNull !== bNull) return aNull ? 1 : -1;
      if (aNull && bNull) return 0;
      const cmp = typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv));
      return dir === "asc" ? cmp : -cmp;
    });
  }, [matched, sort]);
  const visible = cap !== undefined && !showAll ? sorted.slice(0, cap) : sorted;
  const toggleSort = (key: string, numeric = true): void =>
    setSort((s) => (s?.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: numeric ? "desc" : "asc" }));
  return { q, setQ, sort, toggleSort, visible, total: all.length, matched: sorted.length, showAll, setShowAll, cap: cap ?? 0 };
}

/** Search input + a "showing N of M" count, for a card's table toolbar. */
export function ListSearch({ q, setQ, placeholder, matched, total }: { q: string; setQ: (v: string) => void; placeholder: string; matched: number; total: number }): React.ReactElement {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} spellCheck={false}
        style={{ border: "1px solid var(--border)", background: "var(--surface-2)", color: "var(--text)", borderRadius: "var(--r-sm)", padding: "7px 11px", fontSize: 12.5, minWidth: 260, flex: "1 1 360px" }} />
      <span style={{ fontSize: 11.5, color: "var(--faint)" }}>{matched === total ? `${total}` : `${matched} of ${total}`}</span>
    </div>
  );
}

/** Clickable sort header cell. Pass the same `sort`/`onSort` the hook returns. */
export function SortTh({ label, sortKey, sort, onSort, numeric = true, style }: {
  label: string; sortKey: string; sort: SortState | null; onSort: (key: string, numeric?: boolean) => void;
  numeric?: boolean; style: React.CSSProperties;
}): React.ReactElement {
  const active = sort?.key === sortKey;
  return (
    <th onClick={() => onSort(sortKey, numeric)} style={{ ...style, cursor: "pointer", userSelect: "none", color: active ? "var(--text)" : undefined }} title="Click to sort">
      {label}{active ? (sort?.dir === "asc" ? " ↑" : " ↓") : ""}
    </th>
  );
}

/** A full-width table-footer row with a "Show all N" / "Show less" toggle. */
export function SeeMoreRow({ colSpan, matched, cap, showAll, setShowAll, tdStyle }: {
  colSpan: number; matched: number; cap: number; showAll: boolean; setShowAll: (v: boolean) => void; tdStyle: React.CSSProperties;
}): React.ReactElement | null {
  if (matched <= cap) return null;
  return (
    <tr>
      <td colSpan={colSpan} style={{ ...tdStyle, textAlign: "center", padding: "10px" }}>
        <button onClick={() => setShowAll(!showAll)} style={{ border: "none", background: "transparent", color: "var(--accent)", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>
          {showAll ? "Show less" : `Show all ${matched}`}
        </button>
      </td>
    </tr>
  );
}
